import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ChevronLeft,
  MessageSquare,
  ArrowUpCircle,
  CheckCircle2,
  AlertTriangle,
  FileWarning,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { UrgencyBadge } from '@/components/shared/UrgencyBadge';
import { EmptyState } from '@/components/shared/EmptyState';
import { DiffSectionBlock } from '@/components/review/DiffSectionBlock';
import { ApproveConfirmDialog } from '@/components/review/ApproveConfirmDialog';
import { RevisionDialog } from '@/components/review/RevisionDialog';
import { EscalateDialog } from '@/components/review/EscalateDialog';
import { useAuthStore } from '@/stores/authStore';
import { UserRole } from '@/types/auth';
import { api, ApiError } from '@/lib/api';
import type { DiffSection } from '@/types/api';

export function DiffViewPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [approveOpen, setApproveOpen] = useState(false);
  const [revisionOpen, setRevisionOpen] = useState(false);
  const [escalateOpen, setEscalateOpen] = useState(false);

  const canEscalate =
    user?.role === UserRole.COMPLIANCE_REP ||
    user?.role === UserRole.DEPARTMENT_REP;

  const {
    data: pkg,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['review-session', sessionId],
    queryFn: () => api.reviewSessions.get(sessionId!),
    enabled: !!sessionId,
  });

  const selectedSection: DiffSection | null =
    pkg?.sections.find((s) => s.sectionId === selectedSectionId) ?? null;

  // Mutations
  const approveMutation = useMutation({
    mutationFn: ({ notes }: { notes?: string }) =>
      api.reviewSessions.approve(sessionId!, notes),
    onSuccess: () => {
      toast.success('Document approved and queued for publishing');
      navigate('/review');
      queryClient.invalidateQueries({ queryKey: ['review-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
    },
    onError: () => {
      toast.error('Failed to approve. Please try again.');
    },
  });

  const reviseMutation = useMutation({
    mutationFn: ({ notes }: { notes: string }) =>
      api.reviewSessions.requestRevision(sessionId!, notes),
    onSuccess: () => {
      toast.success('Revision requested. A new draft will be generated.');
      setRevisionOpen(false);
      queryClient.invalidateQueries({
        queryKey: ['review-session', sessionId],
      });
    },
    onError: () => {
      toast.error('Failed to request revision. Please try again.');
    },
  });

  const escalateMutation = useMutation({
    mutationFn: ({ notes }: { notes: string }) =>
      api.reviewSessions.escalate(sessionId!, notes),
    onSuccess: () => {
      toast.success('Escalated to CCO.');
      navigate('/review');
    },
    onError: () => {
      toast.error('Failed to escalate.');
    },
  });

  // Loading state
  if (isLoading) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex h-16 items-center gap-4 border-b border-border bg-card px-6">
          <div className="h-5 w-32 animate-pulse rounded bg-muted" />
          <div className="h-5 w-48 animate-pulse rounded bg-muted" />
        </div>
        <div className="flex-1 p-6">
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-4">
              {Array.from({ length: 3 }, (_, i) => (
                <div
                  key={i}
                  className="h-32 animate-pulse rounded-lg bg-muted"
                />
              ))}
            </div>
            <div className="space-y-4">
              {Array.from({ length: 3 }, (_, i) => (
                <div
                  key={i}
                  className="h-32 animate-pulse rounded-lg bg-muted"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error / not found
  if (isError || !pkg) {
    const is404 = error instanceof ApiError && error.status === 404;
    return (
      <div className="flex h-full items-center justify-center">
        <EmptyState
          icon={FileWarning}
          title={is404 ? 'Session not found' : 'Failed to load review'}
          description={
            is404
              ? 'This review session may have been completed or removed.'
              : 'Something went wrong. Please try again.'
          }
          action={
            <Button variant="outline" onClick={() => navigate('/review')}>
              Back to Review Queue
            </Button>
          }
        />
      </div>
    );
  }

  const { session, sections, conflictEscalation } = pkg;

  return (
    <div className="flex h-full flex-col">
      {/* Sticky Header Bar */}
      <div className="flex h-16 shrink-0 items-center justify-between border-b border-border bg-card px-6">
        <div className="flex items-center gap-4 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/review')}
            className="shrink-0"
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Back
          </Button>
          <h1 className="truncate text-lg font-semibold text-foreground">
            {session.documentTitle}
          </h1>
          <span className="inline-flex shrink-0 items-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            {session.department}
          </span>
          <UrgencyBadge score={session.urgencyScore} />
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setRevisionOpen(true)}
          >
            <MessageSquare className="mr-1.5 h-3.5 w-3.5" />
            Request Revision
          </Button>

          {canEscalate && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEscalateOpen(true)}
            >
              <ArrowUpCircle className="mr-1.5 h-3.5 w-3.5" />
              Escalate to CCO
            </Button>
          )}

          <Button
            size="sm"
            className="bg-teal text-white hover:bg-teal/90"
            onClick={() => setApproveOpen(true)}
          >
            <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
            Approve
          </Button>
        </div>
      </div>

      {/* Conflict Alert */}
      {conflictEscalation && (
        <div className="flex items-start gap-3 border-b border-amber-500/30 bg-amber-500/5 px-6 py-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
          <div>
            <p className="text-sm font-medium text-amber-300">
              Regulatory Conflict Detected
            </p>
            <p className="mt-0.5 text-sm text-amber-300/80">
              {conflictEscalation.description}
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {conflictEscalation.affectedSections.map((sId) => {
                const sec = sections.find((s) => s.sectionId === sId);
                return (
                  <span
                    key={sId}
                    className="inline-flex items-center rounded-md bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-400"
                  >
                    {sec?.sectionTitle ?? sId}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Two-panel diff view */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Sticky column headers */}
        <div className="grid shrink-0 grid-cols-2 divide-x divide-border border-b border-border bg-muted/30">
          <div className="flex h-10 items-center px-4">
            <p className="text-sm font-medium text-muted-foreground">
              Current Version
            </p>
          </div>
          <div className="flex h-10 items-center gap-2 px-4">
            <p className="text-sm font-medium text-teal-400">
              Proposed Version
            </p>
            <span className="inline-flex items-center rounded-md bg-teal/15 px-2 py-0.5 text-[10px] font-semibold text-teal">
              AI Draft
            </span>
          </div>
        </div>

        {/* Scrollable section list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {sections.map((section) => (
            <DiffSectionBlock
              key={section.sectionId}
              section={section}
              isSelected={selectedSectionId === section.sectionId}
              onSelect={() =>
                setSelectedSectionId(
                  selectedSectionId === section.sectionId
                    ? null
                    : section.sectionId,
                )
              }
            />
          ))}
        </div>
      </div>

      {/* Dialogs */}
      <ApproveConfirmDialog
        open={approveOpen}
        onOpenChange={setApproveOpen}
        session={session}
        hasConflict={conflictEscalation !== null}
        onConfirm={(notes) => approveMutation.mutate({ notes })}
        isLoading={approveMutation.isPending}
      />

      <RevisionDialog
        open={revisionOpen}
        onOpenChange={setRevisionOpen}
        session={session}
        onConfirm={(notes) => reviseMutation.mutate({ notes })}
        isLoading={reviseMutation.isPending}
      />

      <EscalateDialog
        open={escalateOpen}
        onOpenChange={setEscalateOpen}
        session={session}
        selectedSection={selectedSection}
        onConfirm={(notes) => escalateMutation.mutate({ notes })}
        isLoading={escalateMutation.isPending}
      />
    </div>
  );
}
